import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import * as moment from 'moment'
import { fromEvent, merge, of, timer } from 'rxjs';
import { delay, repeat, switchMap, takeUntil } from 'rxjs/operators';
import { formatAbbreviatedDuration } from 'src/util/formatters';

@Component({
  selector: 'app-datetime-picker',
  templateUrl: './datetime-picker.component.html',
  styleUrls: ['./datetime-picker.component.scss']
})
export class DatetimePickerComponent implements OnInit, AfterViewInit {
  @Input() label!: string;

  @Input()
  upperBound?: moment.Moment;
  @Input()
  lowerBound?: moment.Moment;

  private initialValue: moment.Moment | null = null;
  private _selection: moment.Moment = moment();
  @Input()
  get selection(): moment.Moment { return this._selection; }
  set selection(value: moment.Moment) {
    if (this.initialValue === null) {
      this.initialValue = value;
    }

    if (this.isInBounds(value) && !value.isSame(this.selection)) {
      this._selection = value;
      this.selectionChange.next(this.selection);
    }
  }
  @Output() selectionChange = new EventEmitter<moment.Moment>();

  get selectedDate(): moment.Moment { return this.selection; }
  set selectedDate(value: moment.Moment) {
    const timeComponent = this.selection.diff(moment(this.selection).startOf('d'));
    value.add(timeComponent, 'ms');
    this.selection = value;
  }

  get delta(): string {
    const diff = this.selection.diff(moment(this.initialValue))
    const diffSign = diff > 0
      ? 'later'
      : 'earlier';
    const diffString = formatAbbreviatedDuration(moment.duration(diff));
    return diff === 0 ? 'no change' : `${diffSign} by ${diffString}`;
  }

  get hours(): string { return this.selection.format('HH'); }
  set hours(value: string) { this.selection = moment(this.selection).hours(this.clamp(parseInt(value) || 0, 0, 23)); }
  get minutes(): string { return this.selection.format('mm'); }
  set minutes(value: string) { this.selection = moment(this.selection).minutes(this.clamp(parseInt(value) || 0, 0, 59)); }
  get seconds(): string { return this.selection.format('ss'); }
  set seconds(value: string) { this.selection = moment(this.selection).seconds(this.clamp(parseInt(value) || 0, 0, 59)); }

  private clamp(value: number, min: number, max: number): number { return Math.min(Math.max(min, value), max); }
  private isInBounds = (value: moment.Moment) => (!this.lowerBound || this.lowerBound.isBefore(value)) && (!this.upperBound || this.upperBound.isAfter(value));

  @ViewChild('components')
  componentsContainer: ElementRef<HTMLDivElement> | null = null;

  constructor() { }

  increment = (component: moment.unitOfTime.Base) => this.selection = moment(this.selection).add(1, component);
  decrement = (component: moment.unitOfTime.Base) => this.selection = moment(this.selection).subtract(1, component);

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    const buttons = Array.from(this.componentsContainer?.nativeElement.querySelectorAll<HTMLButtonElement>('button.up, button.down')!);
    buttons.forEach(button => {
      const holdStart = merge(fromEvent(button, "mousedown"), fromEvent(button, "touchstart"));
      const holdEnd = merge(fromEvent(button, "mouseup"), fromEvent(button, "mouseleave"), fromEvent(button, "touchend"), fromEvent(button, "touchcancel"));
      const tickTimer = timer(100);

      const hold = holdStart.pipe(
        delay(400),
        switchMap((downEvent) => {
          return tickTimer.pipe(
            switchMap((time) => of('tick')),
            repeat()
          );
        }),
        takeUntil(holdEnd),
        repeat()
      )

      hold.subscribe(() => button.click());
    });
  }
}
